import { useEffect, useState } from 'react';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import { msaApi } from '../../lib/apiClient.js';

/**
 * Portfolio construction flow: add a position via dropdown+weight, remove
 * via ×, live total-allocation readout that flags ≠100%, normalise to
 * 100%. The dropdown is the REAL MSA universe (fetched via the server
 * proxy), not a hardcoded list.
 */
export default function HoldingsBuilder({ holdings, onChange }) {
  const [universe, setUniverse] = useState([]);
  const [quotes, setQuotes] = useState({});
  const [addSymbol, setAddSymbol] = useState('');
  const [addWeight, setAddWeight] = useState('');

  useEffect(() => {
    let alive = true;
    msaApi
      .universe()
      .then((list) => alive && setUniverse(Array.isArray(list) ? list : []))
      .catch(() => alive && setUniverse([]));
    return () => {
      alive = false;
    };
  }, []);

  // Live quotes for the current holdings (msaApi.quotes — the endpoint exists
  // and is live; this is where the UI finally consumes it).
  useEffect(() => {
    if (holdings.length === 0) {
      setQuotes({});
      return;
    }
    let alive = true;
    msaApi
      .quotes(holdings.map((h) => h.symbol))
      .then((data) => {
        if (!alive) return;
        const map = {};
        for (const it of data?.items || []) map[it.symbol] = it;
        setQuotes(map);
      })
      .catch(() => alive && setQuotes({}));
    return () => {
      alive = false;
    };
  }, [holdings]);

  function currencyOf(symbol) {
    const stock = universe.find((s) => s.symbol === symbol);
    return stock?.currency || '';
  }

  function fmt(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
  }

  const total = holdings.reduce((sum, h) => sum + (Number(h.weight) || 0), 0);
  const totalPct = Math.round(total * 1000) / 10;
  const isBalanced = Math.abs(total - 1) < 0.005;
  const candidateWeight = Number(addWeight);
  const canAdd = Boolean(addSymbol) && Number.isFinite(candidateWeight) && candidateWeight > 0 && candidateWeight <= 100;

  function addPosition() {
    if (!canAdd) return;
    const stock = universe.find((s) => s.symbol === addSymbol);
    if (!stock) return;
    if (holdings.some((h) => h.symbol === addSymbol)) return;
    const weight = Number(addWeight) / 100;
    onChange([...holdings, { symbol: stock.symbol, exchange: stock.exchange, weight }]);
    setAddSymbol('');
    setAddWeight('');
  }

  function removePosition(symbol) {
    onChange(holdings.filter((h) => h.symbol !== symbol));
  }

  function normalise() {
    if (total === 0) return;
    onChange(holdings.map((h) => ({ ...h, weight: Number((h.weight / total).toFixed(4)) })));
  }

  return (
    <Card>
      <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
        Holdings
      </h3>

      {holdings.length === 0 && (
        <div className="aq-empty-state">
          No positions yet. Add one below to start building a portfolio.
        </div>
      )}

      <div className="aq-holdings-list">
        {holdings.map((h) => {
          const q = quotes[h.symbol];
          const change = Number(q?.changePct);
          return (
            <div key={h.symbol} className="aq-holding-row">
              <span className="aq-holding-symbol">
                {h.symbol} <span className="aq-holding-ex">{h.exchange}</span>
              </span>
              <div className="aq-wbar">
                <div className="aq-wbar-fill" style={{ width: `${(h.weight * 100).toFixed(1)}%` }} />
              </div>
              <span className="num aq-holding-weight">{(h.weight * 100).toFixed(1)}%</span>
              <span className="num aq-holding-price" title="Live MSA quote">
                {q ? `${fmt(q.last)} ${currencyOf(h.symbol)}` : '…'}
                {Number.isFinite(change) && (
                  <span className={`num ${change >= 0 ? 'aq-price-up' : 'aq-price-down'}`}>
                    {' '}
                    {change >= 0 ? '+' : ''}
                    {change.toFixed(2)}%
                  </span>
                )}
              </span>
              <button className="aq-holding-remove" onClick={() => removePosition(h.symbol)} aria-label={`Remove ${h.symbol}`}>
                ×
              </button>
            </div>
          );
        })}
      </div>

      <div className="aq-add-pos">
        <select value={addSymbol} onChange={(e) => setAddSymbol(e.target.value)}>
          <option value="">Add a position…</option>
          {universe
            .filter((s) => !holdings.some((h) => h.symbol === s.symbol))
            .map((s) => (
              <option key={s.symbol} value={s.symbol}>
                {s.symbol} — {s.name} ({s.exchange})
              </option>
            ))}
        </select>
        <input
          type="number"
          placeholder="Weight %"
          value={addWeight}
          onChange={(e) => setAddWeight(e.target.value)}
          style={{ width: 90 }}
        />
        <Button size="sm" onClick={addPosition} disabled={!canAdd}>
          Add
        </Button>
      </div>
      {addWeight !== '' && !canAdd && <p className="aq-pf-total-warn">Weight must be greater than 0 and no more than 100%.</p>}

      <div className="aq-pf-total">
        <span>
          Total allocation: <strong className="num">{totalPct}%</strong>
        </span>
        {!isBalanced && holdings.length > 0 && (
          <>
            <span className="aq-pf-total-warn">≠ 100%</span>
            <Button variant="secondary" size="sm" onClick={normalise}>
              Normalise to 100%
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
